import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Input,
  Spinner,
  Checkbox,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { fetchCuratedPlaylists } from '../../../api/libraryApi';

interface PlaylistMultiSelectProps {
  /** Currently selected playlist ids. */
  value: string[];
  /** Called with the next selection whenever a row is toggled. */
  onChange: (ids: string[]) => void;
  /** Disable all inputs (e.g. while an upload is in flight). */
  isDisabled?: boolean;
  /** Suppress the internal label + helper (e.g. when the panel sits inside a drawer with its own header). */
  hideLabel?: boolean;
}

/**
 * Pre-upload multi-select playlist picker. Lets a curator pick one or more curated
 * playlists to drop the uploaded tracks into. Mirrors the multi-select "Add Tracks"
 * feel of the edit-playlist page: a search box plus a scrollable list of checkbox rows.
 */
export function PlaylistMultiSelect({ value, onChange, isDisabled, hideLabel }: PlaylistMultiSelectProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const labelColor = useColorModeValue('navy.700', 'white');
  const secondaryColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const rowHoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  const { data: playlists, isLoading } = useQuery({
    queryKey: ['curated-playlists-picker'],
    queryFn: fetchCuratedPlaylists,
  });

  const filtered = useMemo(() => {
    const list = playlists ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => p.name.toLowerCase().includes(q));
  }, [playlists, search]);

  const toggle = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <Box data-testid="upload-playlist-select">
      {!hideLabel && (
        <>
          <Flex align="center" justify="space-between" mb={1} gap={2}>
            <Text as="label" fontSize="sm" fontWeight="600" color={labelColor}>
              {t('admin.library.uploadToPlaylists.label')}
            </Text>
            {value.length > 0 && (
              <Badge colorScheme="brand" data-testid="upload-playlist-selected-count">
                {t('admin.library.uploadToPlaylists.selected', { count: value.length })}
              </Badge>
            )}
          </Flex>
          <Text fontSize="xs" color={secondaryColor} mb={2}>
            {t('admin.library.uploadToPlaylists.helper')}
          </Text>
        </>
      )}

      <Input
        size="sm"
        borderRadius="10px"
        mb={2}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('admin.library.uploadToPlaylists.search')}
        isDisabled={isDisabled}
        data-testid="upload-playlist-search"
      />

      <Box
        border="1px solid"
        borderColor={borderColor}
        borderRadius="10px"
        maxH="180px"
        overflowY="auto"
      >
        {isLoading ? (
          <Flex justify="center" p={4}>
            <Spinner size="sm" />
          </Flex>
        ) : filtered.length > 0 ? (
          filtered.map((playlist) => (
            <Flex
              key={playlist.id}
              as="label"
              align="center"
              gap={3}
              p="8px 10px"
              cursor={isDisabled ? 'not-allowed' : 'pointer'}
              _hover={{ bg: rowHoverBg }}
              data-testid={`upload-playlist-${playlist.id}`}
            >
              <Checkbox
                isChecked={value.includes(playlist.id)}
                onChange={() => toggle(playlist.id)}
                isDisabled={isDisabled}
              />
              <Text flex={1} fontSize="sm" noOfLines={1} color={labelColor}>
                {playlist.name}
              </Text>
              <Badge colorScheme="gray">
                {t('admin.library.addToPlaylist.trackCount', { count: playlist.trackCount })}
              </Badge>
            </Flex>
          ))
        ) : (
          <Text fontSize="sm" color={secondaryColor} p={3}>
            {t('admin.library.uploadToPlaylists.noPlaylists')}
          </Text>
        )}
      </Box>
    </Box>
  );
}

export default PlaylistMultiSelect;
